import React, { useEffect, useMemo, useState, useRef } from "react";
import { useField, useFormikContext } from "formik";
import { Input, Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
import { ControlButton, IControlStyles, useControlStyles } from "../.";
import { ControlTemplateProps } from "./controlInput";
import { ICON_GLYPHMAPS, IconTypeValue } from "../../../../icons/glyphMaps";
import { FontIcon } from "../../../../icons/FontIcon";

export interface ControlIconPickerProps extends ControlTemplateProps {
    nameType: string;      // form field for icon library
    nameName: string;      // form field for icon name
    previewSize?: number;
    allowClear?: boolean;
    disabled?: boolean;
    iconColor?: string | undefined;
    previewBackgroundColor?: string | undefined;
}

type IconItem = { type: IconTypeValue; name: string };

const INITIAL_CHUNK = 400;
const CHUNK_SIZE = 400;

export const ControlIconPicker: React.FC<ControlIconPickerProps> = (props) => {
    const styles: IControlStyles = props.controlStyles ?? useControlStyles();

    const [, , typeHelpers] = useField<string>(props.nameType);
    const [, , nameHelpers] = useField<string>(props.nameName);

    const { values } = useFormikContext<Record<string, unknown>>();

    const [open, setOpen] = useState(false);
    const currentType = (values?.[props.nameType] ?? "") as IconTypeValue | "";
    const currentName = (values?.[props.nameName] ?? "") as string;
    const iconColor = props.iconColor;
    const previewBackgroundColor = props.previewBackgroundColor ?? "#fff";
    const onClear = () => {
        typeHelpers.setValue("");
        nameHelpers.setValue("");
    };

    // Single search box (only shown in popup)
    const [search, setSearch] = useState("");

    // Flatten all glyph maps into one giant list
    const allIcons: IconItem[] = useMemo(() => {
        const items: IconItem[] = [];
        (Object.entries(ICON_GLYPHMAPS) as [IconTypeValue, Record<string, unknown>][]).forEach(
            ([type, map]) => {
                Object.keys(map ?? {}).forEach((name) => items.push({ type, name }));
            }
        );
        items.sort((a, b) => a.name.localeCompare(b.name) || a.type.localeCompare(b.type));
        return items;
    }, []);

    const filteredIcons = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return allIcons;
        return allIcons.filter((x) => x.name.toLowerCase().includes(q));
    }, [allIcons, search]);

    // Lazy-load chunking for performance
    const [visibleCount, setVisibleCount] = useState(INITIAL_CHUNK);
    const gridRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Reset chunking each time popup opens or search changes
        if (open) setVisibleCount(INITIAL_CHUNK);
    }, [open, search]);

    const onGridScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        const el = e.currentTarget;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
        if (nearBottom && visibleCount < filteredIcons.length) {
            setVisibleCount((c) => Math.min(c + CHUNK_SIZE, filteredIcons.length));
        }
    };

    const openModal = () => {
        if (props.disabled) return;
        setOpen(true);
    };

    const onPickIcon = (item: IconItem) => {
        typeHelpers.setValue(item.type);
        nameHelpers.setValue(item.name);
        setOpen(false);
    };

    const renderSelected = () => {
        const styleWidth = props.style?.width;
        const styleHeight = props.style?.height;
        const defaultBoxWidth =
            (styles.image && typeof (styles.image).width === "number" && (styles.image).width) || 250;
        const defaultBoxHeight =
            (styles.image && typeof (styles.image).height === "number" && (styles.image).height) || 250;
        const boxWidth = styleWidth ?? defaultBoxWidth;
        const boxHeight = styleHeight ?? defaultBoxHeight;
        const numericBoxHeight = typeof boxHeight === "number" ? boxHeight : undefined;

        const size =
            props.previewSize ??
            (numericBoxHeight ? Math.max(24, numericBoxHeight - 40) : 156);

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 24,
                    flexWrap: "wrap",
                }}
            >
                <div
                    style={{
                        ...(styles.image ?? styles.text ?? styles.input),
                        ...(props.style ?? {}),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: boxWidth,
                        height: boxHeight,
                        border: "1px solid #e3e3e3",
                        borderRadius: 8,
                        overflow: "hidden",
                        background: previewBackgroundColor,
                    }}
                >
                    {currentType && currentName ? (
                        <FontIcon
                            type={currentType as IconTypeValue}
                            name={currentName}
                            size={size}
                            style={{ color: iconColor }}
                        />
                    ) : (
                        <span style={{ fontSize: 12 }}>No icon selected</span>
                    )}
                </div>
                <Button
                    type="button"
                    color="primary"
                    outline
                    onClick={openModal}
                    disabled={props.disabled}
                >
                    Change Icon
                </Button>
                {(props.allowClear ?? true) && (
                    <Button
                        type="button"
                        color="secondary"
                        outline
                        onClick={onClear}
                        disabled={props.disabled}
                        aria-label="Clear icon"
                        title="Clear icon"
                    >
                        <i className="bi bi-x-lg" />
                    </Button>
                )}
            </div>
        );
    };

    const renderEmpty = () => (
        <ControlButton
            onClick={openModal}
            style={{ marginTop: 24, ...(styles.button ?? styles.text ?? styles.input), ...(props.style ?? {}) }}
            disabled={props.disabled}
        >
            Select Icon
        </ControlButton>
    );

    const visibleIcons = filteredIcons.slice(0, visibleCount);

    return (
        <>
            {currentName ? renderSelected() : renderEmpty()}

            <Modal isOpen={open} toggle={() => setOpen(false)} size="lg">
                <ModalHeader toggle={() => setOpen(false)}>Choose an Icon</ModalHeader>
                <ModalBody>
                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            marginBottom: 12,
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                        }}
                    >
                        <Input
                            placeholder="Search icons"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ maxWidth: 260 }}
                        />
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                            Showing {visibleIcons.length.toLocaleString()} of{" "}
                            {filteredIcons.length.toLocaleString()} icons
                        </div>
                    </div>

                    <div
                        ref={gridRef}
                        onScroll={onGridScroll}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                            gap: 12,
                            maxHeight: 420,
                            overflow: "auto",
                            paddingRight: 4,
                            border: "1px solid #e4e6ef",
                            borderRadius: 8,
                        }}
                    >
                        {visibleIcons.map((item) => (
                            <button
                                key={`${item.type}:${item.name}`}
                                onClick={() => onPickIcon(item)}
                                title={item.name}
                                style={{
                                    border: "1px solid #e4e6ef",
                                    borderRadius: 8,
                                    padding: 12,
                                    textAlign: "center",
                                    background: "white",
                                    cursor: "pointer",
                                    color: iconColor
                                }}
                            >
                                <div
                                    style={{
                                        height: 36,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <FontIcon type={item.type} name={item.name} size={24} />
                                </div>
                                <div style={{ fontSize: 11, marginTop: 6, wordBreak: "break-word" }}>
                                    {item.name}
                                </div>
                            </button>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};
